import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const AVAILABLE_SCOPES = [
  { id: 'read:tickets', label: 'Read Tickets', category: 'Tickets', description: 'View ticket data' },
  { id: 'write:tickets', label: 'Write Tickets', category: 'Tickets', description: 'Create and update tickets' },
  { id: 'read:invoices', label: 'Read Invoices', category: 'Finance', description: 'View invoice data' },
  { id: 'write:invoices', label: 'Write Invoices', category: 'Finance', description: 'Create and update invoices' },
  { id: 'read:customers', label: 'Read Customers', category: 'Customers', description: 'View customer records' },
  { id: 'write:customers', label: 'Write Customers', category: 'Customers', description: 'Create and update customers' },
  { id: 'read:reports', label: 'Read Reports', category: 'Reports', description: 'Access report data' },
  { id: 'read:personnel', label: 'Read Personnel', category: 'Personnel', description: 'View personnel records' },
  { id: 'read:inventory', label: 'Read Inventory', category: 'Inventory', description: 'View inventory data' },
  { id: 'write:inventory', label: 'Write Inventory', category: 'Inventory', description: 'Update inventory records' },
];

const SCOPE_CATEGORIES = ['Tickets', 'Finance', 'Customers', 'Reports', 'Personnel', 'Inventory'];

const generateApiKey = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const prefix = 'fsk_live_';
  let key = prefix;
  for (let i = 0; i < 40; i++) {
    key += chars?.charAt(Math.floor(Math.random() * chars?.length));
  }
  return key;
};

const maskKey = (prefix) => `${prefix}${'•'?.repeat(32)}`;

const ApiKeyManagement = () => {
  const { orgMembership } = useAuth();
  const [activeTab, setActiveTab] = useState('active');
  const [apiKeys, setApiKeys] = useState([]);
  const [usageLogs, setUsageLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showNewKey, setShowNewKey] = useState(null);
  const [revokeConfirm, setRevokeConfirm] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // Create form state
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyDesc, setNewKeyDesc] = useState('');
  const [newKeyExpiry, setNewKeyExpiry] = useState('');
  const [selectedScopes, setSelectedScopes] = useState([]);
  const [creating, setCreating] = useState('');

  const orgId = orgMembership?.organization_id;

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3500);
  };

  const showError = (msg) => {
    setErrorMsg(msg);
    setTimeout(() => setErrorMsg(''), 4000);
  };

  const fetchApiKeys = async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('api_keys')?.select('*')?.eq('organization_id', orgId)?.order('created_at', { ascending: false });
      if (error) throw error;
      setApiKeys(data || []);
    } catch (err) {
      showError(err?.message || 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsageLogs = async () => {
    if (!orgId) return;
    try {
      const { data, error } = await supabase?.from('api_key_usage_logs')?.select('*, api_keys(name, key_prefix)')?.eq('organization_id', orgId)?.order('created_at', { ascending: false })?.limit(50);
      if (error) throw error;
      setUsageLogs(data || []);
    } catch (err) {
      console.error('Usage logs error:', err);
    }
  };

  useEffect(() => {
    fetchApiKeys();
    fetchUsageLogs();
  }, [orgId]);

  const handleCreateKey = async () => {
    if (!newKeyName?.trim() || selectedScopes?.length === 0) return;
    setCreating(true);
    try {
      const rawKey = generateApiKey();
      const prefix = rawKey?.substring(0, 16);
      // Simple hash for storage (in production use bcrypt via edge function)
      const keyHash = btoa(rawKey)?.substring(0, 64);

      const insertData = {
        organization_id: orgId,
        name: newKeyName?.trim(),
        description: newKeyDesc?.trim() || null,
        key_prefix: prefix,
        key_hash: keyHash,
        scopes: selectedScopes,
        is_active: true,
        expires_at: newKeyExpiry ? new Date(newKeyExpiry)?.toISOString() : null,
      };

      const { data, error } = await supabase?.from('api_keys')?.insert(insertData)?.select()?.single();

      if (error) throw error;

      setShowNewKey({ ...data, rawKey });
      setShowCreateModal(false);
      setNewKeyName('');
      setNewKeyDesc('');
      setNewKeyExpiry('');
      setSelectedScopes([]);
      fetchApiKeys();
      showSuccess('API key created successfully');
    } catch (err) {
      showError(err?.message || 'Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  const handleRevokeKey = async (keyId) => {
    try {
      const { error } = await supabase?.from('api_keys')?.update({ is_active: false, revoked_at: new Date()?.toISOString() })?.eq('id', keyId);
      if (error) throw error;
      setRevokeConfirm(null);
      fetchApiKeys();
      showSuccess('API key revoked successfully');
    } catch (err) {
      showError(err?.message || 'Failed to revoke key');
    }
  };

  const handleDeleteKey = async (keyId) => {
    try {
      const { error } = await supabase?.from('api_keys')?.delete()?.eq('id', keyId);
      if (error) throw error;
      fetchApiKeys();
      showSuccess('API key deleted');
    } catch (err) {
      showError(err?.message || 'Failed to delete key');
    }
  };

  const toggleScope = (scopeId) => {
    setSelectedScopes(prev =>
      prev?.includes(scopeId) ? prev?.filter(s => s !== scopeId) : [...prev, scopeId]
    );
  };

  const activeKeys = apiKeys?.filter(k => k?.is_active);
  const revokedKeys = apiKeys?.filter(k => !k?.is_active);

  const usageByKey = usageLogs?.reduce((acc, log) => {
    const keyId = log?.api_key_id;
    if (!acc?.[keyId]) acc[keyId] = 0;
    acc[keyId]++;
    return acc;
  }, {});

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-6 pb-8 px-4 lg:px-6">
        {/* Page Header */}
        <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground">API Key Management</h1>
            <p className="text-sm text-muted-foreground mt-1">Generate and manage API keys for third-party integrations</p>
          </div>
          <div className="flex items-center gap-3">
            {successMsg && (
              <div className="flex items-center gap-2 px-3 py-2 bg-success/10 border border-success/20 text-success text-sm font-medium rounded-lg">
                <Icon name="CheckCircle" size={14} />
                {successMsg}
              </div>
            )}
            {errorMsg && (
              <div className="flex items-center gap-2 px-3 py-2 bg-error/10 border border-error/20 text-error text-sm font-medium rounded-lg">
                <Icon name="AlertCircle" size={14} />
                {errorMsg}
              </div>
            )}
            <Button
              variant="default"
              size="sm"
              onClick={() => setShowCreateModal(true)}
              iconName="Plus"
              iconPosition="left"
            >
              Generate API Key
            </Button>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Active Keys', value: activeKeys?.length, icon: 'Key', color: 'text-success' },
            { label: 'Revoked Keys', value: revokedKeys?.length, icon: 'KeyRound', color: 'text-error' },
            { label: 'Total API Calls', value: usageLogs?.length, icon: 'Activity', color: 'text-primary' },
            { label: 'Scopes Defined', value: [...new Set(apiKeys?.flatMap(k => k?.scopes || []))]?.length, icon: 'Shield', color: 'text-warning' },
          ]?.map((stat, idx) => (
            <div key={idx} className="bg-card border border-border rounded-xl p-4 shadow-elevation-1">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground font-medium">{stat?.label}</span>
                <Icon name={stat?.icon} size={16} className={stat?.color} />
              </div>
              <p className="text-2xl font-bold text-foreground">{stat?.value ?? 0}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-5 bg-muted/40 p-1 rounded-lg w-fit">
          {[{ id: 'active', label: 'Active Keys', icon: 'Key' }, { id: 'revoked', label: 'Revoked', icon: 'KeyRound' }, { id: 'usage', label: 'Usage Logs', icon: 'Activity' }]?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === tab?.id
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={14} />
              {tab?.label}
            </button>
          ))}
        </div>

        {/* Active Keys Tab */}
        {activeTab === 'active' && (
          <div className="bg-card border border-border rounded-xl shadow-elevation-1">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            ) : activeKeys?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-14 h-14 bg-muted rounded-full flex items-center justify-center mb-3">
                  <Icon name="Key" size={24} className="text-muted-foreground" />
                </div>
                <p className="text-base font-medium text-foreground">No active API keys</p>
                <p className="text-sm text-muted-foreground mt-1">Generate your first API key to get started</p>
                <Button variant="default" size="sm" className="mt-4" onClick={() => setShowCreateModal(true)} iconName="Plus" iconPosition="left">Generate Key</Button>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {activeKeys?.map(key => (
                  <div key={key?.id} className="p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Icon name="Key" size={18} className="text-success" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-semibold text-foreground">{key?.name}</p>
                            {isExpired(key?.expires_at) && (
                              <span className="text-xs px-2 py-0.5 bg-warning/10 text-warning rounded-full">Expired</span>
                            )}
                          </div>
                          {key?.description && <p className="text-xs text-muted-foreground mt-0.5">{key?.description}</p>}
                          <p className="text-xs font-mono text-muted-foreground mt-1.5 bg-muted/50 px-2 py-1 rounded inline-block">{maskKey(key?.key_prefix)}</p>
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {key?.scopes?.map(scope => (
                              <span key={scope} className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-full border border-primary/20">{scope}</span>
                            ))}
                          </div>
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            <span>Created {formatDate(key?.created_at)}</span>
                            {key?.expires_at && <span>Expires {formatDate(key?.expires_at)}</span>}
                            {key?.last_used_at && <span>Last used {formatDate(key?.last_used_at)}</span>}
                            <span className="flex items-center gap-1"><Icon name="Activity" size={11} />{usageByKey?.[key?.id] || 0} calls</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {revokeConfirm === key?.id ? (
                          <>
                            <Button variant="danger" size="xs" onClick={() => handleRevokeKey(key?.id)}>Confirm Revoke</Button>
                            <Button variant="ghost" size="xs" onClick={() => setRevokeConfirm(null)}>Cancel</Button>
                          </>
                        ) : (
                          <Button variant="outline" size="xs" onClick={() => setRevokeConfirm(key?.id)} iconName="ShieldOff" iconPosition="left">Revoke</Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Revoked Keys Tab */}
        {activeTab === 'revoked' && (
          <div className="bg-card border border-border rounded-xl shadow-elevation-1">
            {revokedKeys?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <Icon name="CheckCircle" size={32} className="text-muted-foreground opacity-40 mb-3" />
                <p className="text-sm text-muted-foreground">No revoked keys</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {revokedKeys?.map(key => (
                  <div key={key?.id} className="p-5 opacity-70">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="w-10 h-10 bg-error/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Icon name="KeyRound" size={18} className="text-error" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-foreground">{key?.name}</p>
                          <p className="text-xs font-mono text-muted-foreground mt-1 bg-muted/50 px-2 py-1 rounded inline-block">{maskKey(key?.key_prefix)}</p>
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {key?.scopes?.map(scope => (
                              <span key={scope} className="text-xs px-2 py-0.5 bg-muted text-muted-foreground rounded-full">{scope}</span>
                            ))}
                          </div>
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            <span>Created {formatDate(key?.created_at)}</span>
                            {key?.revoked_at && <span className="text-error">Revoked {formatDate(key?.revoked_at)}</span>}
                          </div>
                        </div>
                      </div>
                      <Button variant="ghost" size="xs" onClick={() => handleDeleteKey(key?.id)} iconName="Trash2" iconPosition="left" className="text-error hover:text-error">Delete</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Usage Logs Tab */}
        {activeTab === 'usage' && (
          <div className="bg-card border border-border rounded-xl shadow-elevation-1">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon name="Activity" size={16} className="text-primary" />
                <h3 className="text-sm font-semibold text-foreground">Recent API Activity</h3>
              </div>
              <span className="text-xs text-muted-foreground">Last 50 requests</span>
            </div>
            {usageLogs?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <Icon name="Activity" size={32} className="text-muted-foreground opacity-40 mb-3" />
                <p className="text-sm text-muted-foreground">No API activity yet</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {usageLogs?.map(log => (
                  <div key={log?.id} className="flex items-center gap-4 px-5 py-3">
                    <span className={`text-xs font-mono font-bold px-2 py-0.5 rounded ${
                      log?.method === 'GET' ? 'bg-blue-100 text-blue-700' :
                      log?.method === 'POST' ? 'bg-green-100 text-green-700' :
                      log?.method === 'DELETE'? 'bg-red-100 text-red-700' : 'bg-muted text-muted-foreground'
                    }`}>{log?.method || 'GET'}</span>
                    <span className="text-xs font-mono text-foreground flex-1 truncate">{log?.endpoint || '/api/v1/data'}</span>
                    <span className={`text-xs font-medium ${
                      log?.status_code >= 200 && log?.status_code < 300 ? 'text-success' :
                      log?.status_code >= 400 ? 'text-error' : 'text-muted-foreground'
                    }`}>{log?.status_code || 200}</span>
                    <span className="text-xs text-muted-foreground">{log?.api_keys?.name || '—'}</span>
                    <span className="text-xs text-muted-foreground">{formatDate(log?.created_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Create Key Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl shadow-elevation-3 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <div className="flex items-center gap-2">
                <Icon name="Key" size={18} className="text-primary" />
                <h2 className="text-base font-semibold text-foreground">Generate New API Key</h2>
              </div>
              <button onClick={() => setShowCreateModal(false)} className="p-1.5 rounded-md hover:bg-muted transition-colors">
                <Icon name="X" size={16} className="text-muted-foreground" />
              </button>
            </div>
            <div className="p-6 space-y-5">
              <Input
                label="Key Name"
                placeholder="e.g. Production Integration"
                value={newKeyName}
                onChange={e => setNewKeyName(e?.target?.value)}
                required
              />
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Description <span className="text-muted-foreground font-normal">(optional)</span></label>
                <textarea
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
                  rows={2}
                  placeholder="What is this key used for?"
                  value={newKeyDesc}
                  onChange={e => setNewKeyDesc(e?.target?.value)}
                />
              </div>
              <Input
                label="Expiry Date"
                type="date"
                value={newKeyExpiry}
                onChange={e => setNewKeyExpiry(e?.target?.value)}
                description="Leave blank for no expiry"
              />

              {/* Scopes */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-foreground">Permissions <span className="text-error">*</span></label>
                  <span className="text-xs text-muted-foreground">{selectedScopes?.length} selected</span>
                </div>
                {SCOPE_CATEGORIES?.map(cat => (
                  <div key={cat}>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{cat}</p>
                    <div className="grid grid-cols-2 gap-2">
                      {AVAILABLE_SCOPES?.filter(s => s?.category === cat)?.map(scope => (
                        <label
                          key={scope?.id}
                          className={`flex items-start gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                            selectedScopes?.includes(scope?.id)
                              ? 'border-primary bg-primary/5' :'border-border hover:border-primary/40'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedScopes?.includes(scope?.id)}
                            onChange={() => toggleScope(scope?.id)}
                            className="mt-0.5 h-3.5 w-3.5 rounded border-input text-primary"
                          />
                          <div>
                            <p className="text-xs font-medium text-foreground">{scope?.label}</p>
                            <p className="text-xs text-muted-foreground">{scope?.description}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  variant="default"
                  className="flex-1"
                  onClick={handleCreateKey}
                  loading={creating}
                  disabled={!newKeyName?.trim() || selectedScopes?.length === 0}
                >
                  Generate Key
                </Button>
                <Button variant="outline" className="flex-1" onClick={() => setShowCreateModal(false)}>Cancel</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Key Display Modal */}
      {showNewKey && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl shadow-elevation-3 w-full max-w-md">
            <div className="px-6 py-4 border-b border-border">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-success/10 rounded-full flex items-center justify-center">
                  <Icon name="CheckCircle" size={16} className="text-success" />
                </div>
                <h2 className="text-base font-semibold text-foreground">API Key Generated</h2>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="p-3 bg-warning/10 border border-warning/30 rounded-lg">
                <div className="flex items-start gap-2">
                  <Icon name="AlertTriangle" size={14} className="text-warning mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-warning font-medium">Copy this key now. It will not be shown again for security reasons.</p>
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1.5">Your API Key</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs font-mono bg-muted/60 border border-border rounded-lg px-3 py-2.5 break-all text-foreground">
                    {showNewKey?.rawKey}
                  </code>
                  <button
                    onClick={() => { navigator.clipboard?.writeText(showNewKey?.rawKey); showSuccess('Copied!'); }}
                    className="p-2 rounded-lg border border-border hover:bg-muted transition-colors flex-shrink-0"
                    title="Copy to clipboard"
                  >
                    <Icon name="Copy" size={14} className="text-muted-foreground" />
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {showNewKey?.scopes?.map(scope => (
                  <span key={scope} className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-full">{scope}</span>
                ))}
              </div>
              <Button variant="default" fullWidth onClick={() => setShowNewKey(null)}>Done</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiKeyManagement;
